/**
 * Botón de descarga con estado y con errores legibles.
 *
 * Sustituye al `<a href>` que apuntaba directo a la API. Ver `download()` en
 * `api/client.ts` para el porqué: un enlace no puede distinguir un archivo de
 * un error, y el libro completo tarda lo bastante como para que la falta de
 * señal se lea como que el botón no funciona.
 */

import { useEffect, useRef, useState } from "react";

import { ApiError, download } from "../api/client";

interface Props {
  /** Ruta de la API que devuelve el archivo. */
  href: string;
  children: React.ReactNode;
  /** `primary` para la acción destacada de la pantalla. */
  variant?: "primary" | "";
  title?: string;
  /** Se llama SOLO si el archivo llegó. Un diálogo que se cierra al pulsar
   *  oculta el error de la descarga que acaba de fallar. */
  onSuccess?: () => void;
}

export function DownloadButton({ href, children, variant = "", title, onSuccess }: Props) {
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Una descarga puede seguir en vuelo cuando el usuario ya cambió de página;
  // un `setState` sobre un componente desmontado avisa por consola y no sirve.
  const vivo = useRef(true);
  useEffect(
    () => () => {
      vivo.current = false;
    },
    [],
  );

  async function pulsar() {
    if (ocupado) return;
    setOcupado(true);
    setError(null);
    try {
      await download(href);
      onSuccess?.();
    } catch (exc) {
      setError(mensaje(exc));
    } finally {
      if (vivo.current) setOcupado(false);
    }
  }

  return (
    <span className="download">
      <button
        type="button"
        className={`btn ${variant}`.trim()}
        onClick={() => {
          void pulsar();
        }}
        disabled={ocupado}
        title={title}
        aria-busy={ocupado}
      >
        {ocupado ? "Generando…" : children}
      </button>
      {error && (
        <span className="download-error" role="status">
          {error}
        </span>
      )}
    </span>
  );
}

function mensaje(exc: unknown): string {
  if (exc instanceof ApiError) {
    if (exc.isForbidden) return `Sin permiso: ${exc.message}`;
    if (exc.isUnauthenticated) return "Su sesión caducó. Vuelva a entrar.";
    if (exc.isNotReady) return "Todavía no hay datos para ese período.";
    if (exc.isDegraded) return "No se pudo validar su sesión. Reintente.";
    return exc.message;
  }
  // Una descarga larga que se corta por red llega aquí, no como ApiError.
  return exc instanceof Error ? exc.message : "No se pudo descargar.";
}
